<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>

    <vx-card class="mt-base mb-10">
      <h4 class="mb-5">Let’s start your virtual job experience in Engineering!</h4>
      <p class="mb-5">
        Engineering is all about problem-solving. Engineers use maths and science to figure out how
        things work and find practical solutions for practical problems.
      </p>
      <p class="mb-5">
        There are many different types of engineers, but they all have in common that they take
        innovations from science and maths and apply that knowledge to the real world.
      </p>
      <p class="mb-5">
        To achieve these engineering feats, engineers design, evaluate, develop, test, modify,
        install, inspect, and maintain a wide variety of systems and products in a wide number of
        specialist areas.
      </p>
    </vx-card>

    <vx-card class="mt-base mb-10">
      <table class="table default-table">
        <thead>
          <th style="font-size: 1rem; font-weight: 500">The main types of engineering are:</th>
        </thead>
        <tr>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/aerospace-engineer"
              target="_blank"
              >Aerospace</a
            >
          </td>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/agricultural-engineer"
              target="_blank"
              >Agricultural</a
            >
          </td>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/biomedical-engineer"
              target="_blank"
              >Biomedical</a
            >
          </td>
        </tr>
        <tr>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/chemical-engineer"
              target="_blank"
              >Chemical</a
            >
          </td>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/civil-engineer"
              target="_blank"
              >Civil</a
            >
          </td>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/computer-engineer"
              target="_blank"
              >Computer</a
            >
          </td>
        </tr>
        <tr>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/electrical-engineer"
              target="_blank"
              >Electrical/Electronics</a
            >
          </td>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/environmental-engineer"
              target="_blank"
              >Environmental</a
            >
          </td>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/geological-engineer"
              target="_blank"
              >Geological</a
            >
          </td>
        </tr>
        <tr>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/industrial-engineer"
              target="_blank"
              >Industrial</a
            >
          </td>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/marine-engineer"
              target="_blank"
              >Marine</a
            >
          </td>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/materials-engineer"
              target="_blank"
              >Materials</a
            >
          </td>
        </tr>
        <tr>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/mechanical-engineer"
              target="_blank"
              >Mechanical</a
            >
          </td>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/mechatronic-engineer"
              target="_blank"
              >Mechatronic</a
            >
          </td>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/mining-engineer"
              target="_blank"
              >Mining</a
            >
          </td>
        </tr>
        <tr>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/petroleum-engineer"
              target="_blank"
              >Petroleum</a
            >
          </td>
          <td>
            <a
              href="https://www.gooduniversitiesguide.com.au/careers-guide/telecommunications-engineer"
              target="_blank"
              >Telecommunications</a
            >
          </td>
        </tr>
      </table>

      <p class="mb-5">
        These can often be broken down into further engineering specialisations – for example,
        Computer Engineering has specialisations in Hardware, Software, Systems and Network
        Engineering.
      </p>

      <p class="mb-5">In this Job Experience, we will be focus on the following 4 areas:</p>
      <ul class="default-list">
        <li>Civil Engineering</li>
        <li>Mechanical Engineering</li>
        <li>Electrical Engineering</li>
        <li>Chemical Engineering</li>
      </ul>
      <p class="mb-5">
        You can use the tabs below to find out more about each of these types of engineering.
      </p>
    </vx-card>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Civil Engineer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <img
              src="@/assets/images/experiences/engineering/civil_engineer_intro.jpg"
              alt=""
              class="my-2 ml-2 mb-2 float-right img-responsive xl:w-1/3 lg:w-1/3 md:w-1/3 sm:w-full"
            />
            <h4 class="mb-5">Description</h4>
            <p class="mb-5">
              Civil Engineers design, plan, organise and oversee the construction of civil
              engineering projects such as dams, bridges, pipelines, gas and water supply schemes,
              sewerage systems, roads, airports and other structures; analyse the likely behaviour
              of soil and rock when placed under pressure by proposed structures and design
              structural foundations; analyse the statistical properties of all types of structures
              and test the behaviour and durability of materials used in their construction; plan
              and develop transportation systems; and estimate and monitor the construction costs of
              projects.
            </p>
            <h4 class="mb-5">Education Level</h4>
            <p class="mb-5">
              You usually need a bachelor's degree in a related field to work as a Civil Engineer.
              It is also common to complete postgraduate studies.
            </p>
            <p class="mb-5">
              Registration may be required in some states and territories. In addition, Engineers
              Australia has a non-compulsory National Engineering Register.
            </p>
            <p class="mb-5">
              Before starting a course, check it will provide you with the skills and qualifications
              you need. Visit:
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> to search
              and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Mechanical Engineer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <img
              src="@/assets/images/experiences/engineering/mechanical_engineer_intro.jpg"
              alt=""
              class="my-2 ml-2 mb-2 float-right img-responsive xl:w-1/3 lg:w-1/3 md:w-1/3 sm:w-full"
            />
            <h4 class="mb-5">Description</h4>
            <p class="mb-5">
              Industrial, Mechanical and Production Engineers design, organise, and oversee the
              construction, operation, and maintenance of mechanical and process plant and
              installations, establish programs for the coordination of manufacturing activities,
              and ensure the usage of resources is cost-effective.
            </p>
            <h4 class="mb-5">Education Level</h4>
            <p class="mb-5">
              You need a bachelor’s degree in a relevant engineering discipline to work as a
              Mechanical Engineer. Postgraduate studies may also be useful.
            </p>
            <p class="mb-5">
              Registration may be required in some states and territories. In addition, Engineers
              Australia has a non-compulsory National Engineering Register.
            </p>
            <p class="mb-5">
              Before starting a course, check it will provide you with the skills and qualifications
              you need. Visit:
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> to search
              and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Electrical Engineer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <img
              src="@/assets/images/experiences/engineering/electrical_engineer_intro.jpg"
              alt=""
              class="my-2 ml-2 mb-2 float-right img-responsive xl:w-1/3 lg:w-1/3 md:w-1/3 sm:w-full"
            />
            <h4 class="mb-5">Description</h4>
            <p class="mb-5">
              Electrical Engineers design, develop and supervise the manufacture, installation,
              operation and maintenance of equipment, machines and systems for the generation,
              distribution, utilisation and control of electric power.
            </p>
            <h4 class="mb-5">Education Level</h4>
            <p class="mb-5">
              You need a bachelor's degree in engineering majoring in electrical or a related field
              to work as an Electrical Engineer. Postgraduate studies may also be useful.
            </p>
            <p class="mb-5">
              Registration may be required in some states and territories. In addition, Engineers
              Australia has a non-compulsory National Engineering Register.
            </p>
            <p class="mb-5">
              Before starting a course, check it will provide you with the skills and qualifications
              you need. Visit:
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> to search
              and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Chemical Engineer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <img
              src="@/assets/images/experiences/engineering/chemical_engineer_intro.jpg"
              alt=""
              class="my-2 ml-2 mb-2 float-right img-responsive xl:w-1/3 lg:w-1/3 md:w-1/3 sm:w-full"
            />
            <h4 class="mb-5">Description</h4>
            <p class="mb-5">
              Chemical and Materials Engineers design and prepare specifications for chemical
              process systems and the construction and operation of commercial-scale chemical
              plants, supervise industrial processing and fabrication of products undergoing
              physical and chemical change, and investigate the properties of metals, ceramics,
              polymers, and other materials and assess and develop their engineering and commercial
              applications.
            </p>
            <h4 class="mb-5">Education Level</h4>
            <p class="mb-5">
              You need a bachelor’s degree in materials, chemical, biochemical, or process
              engineering to work as a Chemical Engineer. It is also common to complete postgraduate
              studies.
            </p>
            <p class="mb-5">
              Registration may be required in some states and territories. In addition, Engineers
              Australia has a non-compulsory National Engineering Register.
            </p>
            <p class="mb-5">
              Before starting a course, check it will provide you with the skills and qualifications
              you need. Visit:
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> to search
              and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>

    <h4 class="mb-5">Quick Details:</h4>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Civil Engineer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <ul class="default-list">
                  <li><strong>Size: </strong>This is a very large occupation.</li>
                  <li>
                    <strong>Earnings: </strong>Full-time workers on an adult wage earn around $1,962
                    per week (higher than the average of $1,460). Earnings tend to be lower when
                    starting out and higher as experience grows.
                  </li>
                  <li>
                    <strong>Full-time: </strong>Most work full-time (90%, much higher than the
                    average of 66%).
                  </li>
                  <li>
                    <strong>Hours: </strong>Full-time workers spend around 46 hours per week at work
                    (compared to the average of 44 hours).
                  </li>
                  <li>
                    <strong>Age: </strong>The average age is 36 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender: </strong>12% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list mb-5">
                  <li>
                    Determining construction methods, materials and quality standards, and drafting
                    and interpreting specifications, drawings, plans, construction methods and
                    procedures
                  </li>
                  <li>
                    Organising and directing site labour and the delivery of construction materials,
                    plant and equipment, and establishing detailed programs for the coordination of
                    site activities
                  </li>
                  <li>
                    Obtaining soil and rock samples at different depths across sites and testing
                    samples to determine strength, compressibility and other factors that affect the
                    behaviour of soil and rock when a structure is imposed and determining the safe
                    loading for the soil
                  </li>
                  <li>
                    Studying architectural and engineering drawings and specifications to estimate
                    total costs, and preparing detailed cost plans and estimates as tools to assist
                    in budgetary control
                  </li>
                  <li>
                    Monitoring changes to designs, assessing effects on cost, and measuring, valuing
                    and negotiating variations to designs
                  </li>
                  <li>Analysing structural systems for both static and dynamic loads</li>
                  <li>
                    Designing structures to ensure they do not collapse, bend, twist or vibrate in
                    undesirable ways
                  </li>
                  <li>
                    Assessing present and future travel flow patterns taking into account population
                    increase and needs change
                  </li>
                  <li>
                    Designing the physical aspects of transportation systems such as highways,
                    railroads, urban transit, air transportation, logistical supply systems and
                    their terminals
                  </li>
                </ul>
                <p class="mb-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/engineering/civil_engineering_stats.png"
                  alt="All Civil Engineering Professionals: $1,962 weekly pay, very strong future growth, 56,100 workers, very high skill level rating, 90% full-time, 46 hours average full-time week, average age 36, 12% female."
                  class="mx-auto img-responsive"
                />
              </div>
            </div>
            <vs-alert color="dark" title="Caution" active="true">
              The Australian jobs market is changing in response to the COVID-19 pandemic. These
              estimates do not take account of the impact of COVID-19. They may not reflect the
              current jobs market and should be used and interpreted with extreme caution.
            </vs-alert>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Mechanical Engineer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <ul class="default-list">
                  <li><strong>Size: </strong>This is a large occupation.</li>
                  <li>
                    <strong>Earnings: </strong>Full-time workers on an adult wage earn around $2,414
                    per week (higher than the average of $1,460). Earnings tend to be lower when
                    starting out and higher as experience grows.
                  </li>
                  <li>
                    <strong>Full-time: </strong>Most work full-time (92%, much higher than the
                    average of 66%).
                  </li>
                  <li>
                    <strong>Hours: </strong>Full-time workers spend around 45 hours per week at work
                    (compared to the average of 44 hours).
                  </li>
                  <li>
                    <strong>Age: </strong>The average age is 38 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender: </strong>7% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list mb-5">
                  <li>
                    Studying functional statements, organisational charts and project information to
                    determine functions and responsibilities of workers and work units and to
                    identify areas of duplication
                  </li>
                  <li>
                    Establishing work measurement programs and analysing work samples to develop
                    standards for labour utilisation
                  </li>
                  <li>
                    Analysing workforce utilisation, facility layout, operational data and
                    production schedules and costs to determine optimum worker and equipment
                    efficiencies
                  </li>
                  <li>
                    Designing mechanical equipment, machines, components, products for manufacture,
                    and plant and systems for construction
                  </li>
                  <li>
                    Developing specifications for manufacture, and determining materials, equipment,
                    piping, material flows, capacities and layout of plant and systems
                  </li>
                  <li>
                    Organising and managing project labour and the delivery of materials, plant and
                    equipment
                  </li>
                  <li>
                    Establishing standards and policies for installation, modification, quality
                    control, testing, inspection and maintenance according to engineering principles
                    and safety regulations
                  </li>
                  <li>Inspecting plant to ensure optimum performance is maintained</li>
                  <li>
                    Directing the maintenance of plant buildings and equipment, and coordinating the
                    requirements for new designs, surveys and maintenance schedules
                  </li>
                </ul>
                <p class="mb-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
                <vs-alert color="dark" title="Caution" active="true">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/engineering/mechanical_engineer_stats.png"
                  alt="All Industrial, Mechanical and Production Engineers: $2,414 weekly pay, stable future growth, 30,100 workers, very high skill level rating, 92% full-time, 45 hours average full-time week, average age 38, 7% female."
                  class="mx-auto img-responsive"
                />
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Electrical Engineer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <ul class="default-list">
                  <li><strong>Size: </strong>This is a medium sized occupation.</li>
                  <li>
                    <strong>Earnings: </strong>Full-time workers on an adult wage earn around $2,160
                    per week (higher than the average of $1,460). Earnings tend to be lower when
                    starting out and higher as experience grows.
                  </li>
                  <li>
                    <strong>Full-time: </strong>Most work full-time (91%, much higher than the
                    average of 66%).
                  </li>
                  <li>
                    <strong>Hours: </strong>Full-time workers spend around 44 hours per week at work
                    (compared to the average of 44 hours).
                  </li>
                  <li>
                    <strong>Age: </strong>The average age is 39 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender: </strong>7% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list mb-5">
                  <li>Planning and designing power stations and power generation equipment</li>
                  <li>
                    Determining the type and arrangement of circuits, transformers,
                    circuit-breakers, transmission lines and other equipment
                  </li>
                  <li>
                    Developing products such as electric motors, components, equipment and
                    appliances
                  </li>
                  <li>
                    Interpreting specifications, drawings, standards and regulations relating to
                    electric power equipment and use
                  </li>
                  <li>
                    Organising and managing resources used in the supply of electrical components,
                    machines, appliances and equipment
                  </li>
                  <li>
                    Establishing delivery and installation schedules for machines, switchgear,
                    cables and fittings
                  </li>
                  <li>
                    Supervising the operation and maintenance of power stations, transmission and
                    distribution systems and industrial plants
                  </li>
                  <li>
                    Designing and installing control and signalling equipment for road, rail and air
                    traffic
                  </li>
                  <li>
                    May specialise in research in areas such as power generation and transmission
                    systems, transformers, switchgear and electric motors, telemetry and control
                    systems
                  </li>
                </ul>
                <p class="mb-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
                <vs-alert color="dark" title="Caution" active="true">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/engineering/electrical_engineer_stats.png"
                  alt="All Electrical Engineers: $2,160 weekly pay, moderate future growth, 16,600 workers, very high skill level rating, 91% full-time, 44 hours average full-time week, average age 39, 7% female."
                  class="mx-auto img-responsive"
                />
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Chemical Engineer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <ul class="default-list">
                  <li><strong>Size: </strong>This is a small occupation.</li>
                  <li>
                    <strong>Earnings: </strong>Full-time workers on an adult wage earn around $3,019
                    per week (higher than the average of $1,460). Earnings tend to be lower when
                    starting out and higher as experience grows.
                  </li>
                  <li>
                    <strong>Full-time: </strong>Most work full-time (88%, much higher than the
                    average of 66%).
                  </li>
                  <li>
                    <strong>Hours: </strong>Full-time workers spend around 44 hours per week at work
                    (compared to the average of 44 hours).
                  </li>
                  <li>
                    <strong>Age: </strong>The average age is 38 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender: </strong>20% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list mb-5">
                  <li>
                    Preparing designs for chemical process systems and planning control systems for
                    processes such as those used to remove and separate components, effect chemical
                    changes, test and evaluate fuels, transfer heat, and control the storing and
                    handling of solids, liquids and gases
                  </li>
                  <li>
                    Monitoring the operation and maintenance of equipment to achieve maximum
                    efficiency under safe operating conditions
                  </li>
                  <li>
                    Ensuring correct materials and equipment are used and that they conform to
                    specifications
                  </li>
                  <li>
                    Diagnosing malfunctions in chemical plants and instituting remedial action
                  </li>
                  <li>Studying product utilisation and pollution control problems</li>
                  <li>
                    Reviewing plans for new products and submitting material selection
                    recommendations in accordance with design specifications and factors such as
                    strength, weight and cost
                  </li>
                  <li>
                    Planning and implementing laboratory operations to develop new materials and
                    fabrication procedures for new materials to fulfil production cost and
                    performance standards
                  </li>
                  <li>
                    Conferring with producers of materials, such as metals, ceramics, polymers,
                    cements and elastomers, during the investigation and evaluation of materials
                    suitable for specific product applications
                  </li>
                  <li>
                    Reviewing product failure data and implementing laboratory tests to establish or
                    reject possible causes, and advising on ways to overcome any problems
                  </li>
                </ul>
                <p class="mb-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
                <vs-alert color="dark" title="Caution" active="true">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/engineering/chemical_engineer_stats.png"
                  alt="All Chemical and Materials Engineers: $3,019 weekly pay, moderate future growth, 6,500 workers, very high skill level rating, 88% full-time, 44 hours average full-time week, average age 38, 20% female."
                  class="mx-auto img-responsive"
                />
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>
  </div>
</template>

<script>
export default {
  data() {
    return {
      popupActive: false,
      image1: require('@/assets/images/experiences/gamesdeveloper/intro1.png'),
      activeTab: 0,
    };
  },
};
</script>
